import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { vectorMin } from '../../assets/assets';
import { gigs, categories, admins } from '../../utils/data';
import Select from '../../utils/Select';
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import CustomQuoteBanner from '../../components/CustomQuoteBanner'
import DeliverySelect from '../../utils/DeliverySelect';
import Actions from '../../components/Actions'

const TodayGigs = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGigs, setFilteredGigs] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [slidingIndexes, setSlidingIndexes] = useState({});
  const [activeHoverCard, setActiveHoverCard] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [maxCategoryIndex, setMaxCategoryIndex] = useState(0);
  const categorySliderRef = useRef(null);
  const itemsPerPage = 12;

  // Format category slug to display name
  const formatCategoryName = (slug) => {
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Initialize category title
  useEffect(() => {
    if (category) {
      setCategoryTitle(formatCategoryName(category));
    } else {
      setCategoryTitle("Today's HandPicked");
    }
  }, [category]);

  // Filter gigs based on category and search term
  useEffect(() => {
    // Create a new function to filter gigs to avoid affecting focus
    const filterGigs = () => {
      let filtered = [...gigs];
      
      // Filter by category if specified, but ensure each category has services to show
      if (category && category !== 'all-services') {
        // For displaying dummy data in every category, we'll just modify filtering
        // Instead of strictly filtering by category, we'll use a combination approach:
        // 1. First try to find services that actually belong to the category
        const categoryServices = filtered.filter(gig => {
          if (!gig.categories || !Array.isArray(gig.categories)) return false;
          return gig.categories.some(cat => {
            if (!cat || !cat.name) return false;
            const catSlug = cat.name.toLowerCase().replace(/\s+/g, '-');
            return catSlug === category;
          });
        });
        
        // 2. If there are enough services (8+), use those
        if (categoryServices.length >= 8) {
          filtered = categoryServices;
        } 
        // 3. Otherwise, show a mix of services but "tag" them with the current category
        else {
          // For dummy data purposes, show all services but make it appear they belong to this category
          // In a real API implementation, you would properly filter by category instead
          const currentCategoryName = formatCategoryName(category);
          
          // Tag the first 20 services with this category if they don't have it already
          filtered = filtered.slice(0, 20).map(gig => {
            // Create a deep copy to avoid mutating the original data
            const gigCopy = { ...gig };
            
            // Ensure categories exists and is an array
            if (!gigCopy.categories || !Array.isArray(gigCopy.categories)) {
              gigCopy.categories = [];
            }
            
            // Add current category if not already present
            const hasCategoryAlready = gigCopy.categories.some(cat => 
              cat && cat.name && cat.name.toLowerCase().replace(/\s+/g, '-') === category
            );
            
            if (!hasCategoryAlready) {
              // Find the category object from categories array
              const categoryObj = categories.find(cat => 
                cat.name.toLowerCase().replace(/\s+/g, '-') === category
              );
              
              if (categoryObj) {
                gigCopy.categories.push(categoryObj);
              } else {
                // If not found in categories, create a new one
                gigCopy.categories.push({
                  id: `temp-${category}`,
                  name: currentCategoryName
                });
              }
            }
            
            return gigCopy;
          });
        }
      }
      
      // Filter by search term if entered
      if (searchTerm.trim() !== '') {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(gig => 
          gig.title.toLowerCase().includes(searchLower) ||
          (gig.description && gig.description.toLowerCase().includes(searchLower))
        );
      }
      
      // Handle Delivery Sort/Filter from URL
      const urlParams = new URLSearchParams(location.search);
      const deliveryParam = urlParams.get('delivery');
      
      if (deliveryParam) {
        // Sort: Bring matching delivery rates to the top
        filtered.sort((a, b) => {
          const aMatch = a.deliveries?.some(d => d.rate.toLowerCase().replace(/\s+/g, '') === deliveryParam.toLowerCase());
          const bMatch = b.deliveries?.some(d => d.rate.toLowerCase().replace(/\s+/g, '') === deliveryParam.toLowerCase());
          if (aMatch && !bMatch) return -1;
          if (!bMatch && aMatch) return 1;
          return 0;
        });
      }
      
      setFilteredGigs(filtered);
      // Reset to first page when filters change
      setCurrentPage(1);
    };
    
    // Execute the filtering function
    filterGigs();
  }, [category, searchTerm, categories, location.search]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Calculate max category index for slider
      if (!mobile && categorySliderRef.current) {
        const maxScrollableIndex = Math.max(0, categories.length - 6);
        setMaxCategoryIndex(maxScrollableIndex);
      }
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Only set up sliding for the card being hovered
    let slidingInterval;
    if (activeHoverCard !== null) {
      slidingInterval = setInterval(() => {
        setSlidingIndexes(prevIndexes => {
          const newIndexes = { ...prevIndexes };
          const gig = gigs.find(g => g.id === activeHoverCard);
          if (gig && gig.thumbnails && gig.thumbnails.length > 1) {
            const currentIndex = prevIndexes[activeHoverCard] || 0;
            const thumbnailsLength = gig.thumbnails.length;
            newIndexes[activeHoverCard] = (currentIndex + 1) % thumbnailsLength;
          }
          return newIndexes;
        });
      }, 1500); // Change thumbnail every 1.5 seconds while hovering
    }
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (slidingInterval) clearInterval(slidingInterval);
    };
  }, [categories.length, activeHoverCard]);

  // Get admin details
  const getAdminDetails = (adminId) => {
    const admin = admins.find(admin => admin.id === adminId);
    return admin || null;
  };

  // Get lowest price from pricing plans
  const getLowestPrice = (pricingPlans) => {
    if (!pricingPlans || pricingPlans.length === 0) return 0;
    
    let lowestPrice = Infinity;
    pricingPlans.forEach(plan => {
      const planPrice = plan.price || 
        (plan.title === "Basic" || plan.title === "Starter" ? 5 :
         plan.title === "Standard" || plan.title === "Business" ? 10 :
         plan.title === "Advanced" || plan.title === "Enterprise" ? 15 :
         plan.title === "Pro" ? 20 :
         plan.title === "Premium" ? 25 : 5);
      
      if (planPrice < lowestPrice) {
        lowestPrice = planPrice;
      }
    });
    
    return lowestPrice === Infinity ? 5 : lowestPrice;
  };

  // Pagination functions
  const totalPages = Math.ceil(filteredGigs.length / itemsPerPage);
  
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Category slider navigation
  const nextCategorySlide = () => {
    setCurrentCategoryIndex((prevIndex) =>
      prevIndex >= maxCategoryIndex ? 0 : prevIndex + 1
    );
  };

  const prevCategorySlide = () => {
    setCurrentCategoryIndex((prevIndex) =>
      prevIndex === 0 ? maxCategoryIndex : prevIndex - 1
    );
  };

  const handleCategoryClick = (categoryName) => {
    const slug = categoryName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/category/${slug}`);
  };

  // Search handler
  const handleSearch = (e) => {
    // Use the current value from the event
    const value = e.target.value;
    setSearchTerm(value);
    // We're not doing anything that should cause the input to lose focus
  };

  // Get paginated gigs
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGigs = filteredGigs.slice(startIndex, endIndex);

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="w-full flex justify-center mt-8 mb-10">
        <div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
          <button 
            onClick={prevPage}
            className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={isMobile ? 16 : 20} />
          </button>
          
          {currentPage > 1 && (
            <button 
              onClick={() => goToPage(1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              1
            </button>
          )}
          
          {currentPage > 3 && <span className="px-1 text-gray-400">...</span>}
          
          {currentPage > 2 && (
            <button 
              onClick={() => goToPage(currentPage - 1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button 
            className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full bg-blue-600 text-white flex items-center justify-center`}
          >
            {currentPage}
          </button>
          
          {currentPage < totalPages - 1 && (
            <button 
              onClick={() => goToPage(currentPage + 1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 2 && <span className="px-1 text-gray-400">...</span>}
          
          {currentPage < totalPages && (
            <button 
              onClick={() => goToPage(totalPages)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {totalPages}
            </button>
          )}
          
          <button 
            onClick={nextPage}
            className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={isMobile ? 16 : 20} />
          </button>
        </div>
      </div>
    );
  };

  // Category Tabs Component
  const CategoryTabs = () => {
    return (
      <div className="my-8 relative md:w-[60%] mx-auto">
      
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <div 
                key={cat.id}
                className={`${isMobile ? 'min-w-[33%] px-1' : 'min-w-[16.666%] px-2'} flex-shrink-0 cursor-pointer`}
                onClick={() => handleCategoryClick(cat.name)}
              >
                <div className={`py-2 px-3 text-center   transition-colors rounded-[30px] bg-white hover:bg-gray-200 text-gray-700 ${
                  category === cat.name.toLowerCase().replace(/\s+/g, '-') ? 'border-[1px] border-[#185BC3]' : ''
                }`}>
                  <span className={`${isMobile ? 'text-[12px]' : 'text-[14px]'} font-regular truncate block`}>{cat.name}</span>
                </div>
              </div>
            ))}
          </div>
</div>
        

    );
  };

  // Service Card Component
  const ServiceCard = ({ gig }) => {
    const admin = getAdminDetails(gig.admin_id);
    const currentThumbnailIndex = slidingIndexes[gig.id] || 0;
    const lowestPrice = getLowestPrice(gig.pricingPlans);
    
    // Generate initials for avatar fallback
    const getInitials = (name) => {
      if (!name) return "U";
      return name.charAt(0).toUpperCase();
    };
    
    return (
      <div 
        className="flex flex-col"
        onMouseEnter={() => setActiveHoverCard(gig.id)}
        onMouseLeave={() => setActiveHoverCard(null)}
      >
        <div className="relative rounded-lg overflow-hidden mb-3 hover:shadow-md transition-shadow duration-300">
          <a href={`/gigs/${gig.title.replace(/\s+/g, '-')}`}>
            {gig.thumbnails && gig.thumbnails.length > 0 ? (
              <img 
                src={gig.thumbnails[currentThumbnailIndex].img} 
                alt={gig.title} 
                className={`w-full ${isMobile ? 'h-36' : 'h-44'} object-cover transition-all duration-500`}
              />
            ) : (
              <div className={`w-full ${isMobile ? 'h-36' : 'h-44'} bg-gray-200 flex items-center justify-center`}>
                <span className="text-gray-500">No Image</span>
              </div>
            )}
          </a>
          {gig.thumbnails && gig.thumbnails.length > 1 && (
            <div className="absolute bottom-2 right-2 flex space-x-1">
              {gig.thumbnails.map((_, index) => (
                <span 
                  key={index}
                  className={`${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'} bg-white rounded-full ${
                    index === currentThumbnailIndex ? 'opacity-100' : 'opacity-60'
                  }`}
                ></span>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center mb-2">
          {admin && admin.profilePicture ? (
            <img 
              src={admin.profilePicture} 
              alt={admin.name} 
              className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full mr-2`}
            />
          ) : (
            <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full mr-2 bg-blue-600 flex items-center justify-center text-white text-xs`}>
              {admin ? getInitials(admin.name) : "U"}
            </div>
          )}
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>{admin ? admin.username : "Unknown"}</span>
        </div>
        
        <a href={`/gigs/${gig.title.replace(/\s+/g, '-')}`} className="hover:text-blue-900">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 line-clamp-2`}>{gig.title}</h3>
        </a>
        
        <div className="flex items-center mb-1">
          <span className="text-[#000000]">★</span>
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ml-1`}>{gig.rating}</span>
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 ml-1`}>({gig.sells})</span>
        </div>
        
        <div className="mt-auto">
          <p className={`${isMobile ? 'text-xs' : 'text-sm'}`}>From ${lowestPrice}</p>
        </div>
      </div>
    );
  };

  // Desktop View
  const DesktopView = () => {
    // Create a ref for the search input
    const searchInputRef = useRef(null);
    
    return (
      <>
      <Header/>
      <div className="max-w-7xl mx-auto px-4 py-8 bg-[#F5F5F5]">
        <div className="flex flex-col">

            <div className="relative w-[70%] mx-auto bg-[#F9F9FF] rounded-full overflow-hidden">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for service"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2  focus:outline-none focus:ring-0 focus:ring-none focus:border-transparent"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Search size={18} />
                </div>
                  <button className="absolute right-2 top-1/2 transform bg-[#174568] -translate-y-1/2 text-white px-2 py-1 rounded-full">
                  Go
                </button>
              </div>



 <CategoryTabs />
<div className="bg-white py-5">
      <Actions/>

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-[#666464]">{categoryTitle}</h1>
            <div className="flex items-center">
              <p className="text-[#174568] mr-4">{filteredGigs.length} services</p>
            
            </div>
          </div>

     
          {filteredGigs.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-gray-700 mb-2">No services found</h3>
              <p className="text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-6 mb-8">
                {paginatedGigs.map(gig => (
                  <ServiceCard key={gig.id} gig={gig} />
                ))}
              </div>
             
              {renderPagination()}
            </>
          )}
           </div>

        </div>
      </div>
      <CustomQuoteBanner/>
      <Footer/>
      </>
    );
  };

  // Mobile View
  const MobileView = () => {
    // Create a ref for the mobile search input
    const mobileSearchInputRef = useRef(null);
    
    return (
      <>
      <Header/>
      <div className="px-0 py-6 bg-[#F5F5F5]">
        <div className="flex flex-col">
          <div className="relative px-4 w-[90%] mx-auto bg-[#F9F9FF] rounded-full overflow-hidden">
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  placeholder="Search for service"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2  focus:outline-none focus:ring-0 focus:ring-none focus:border-transparent"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Search size={18} />
                </div>
                  <button className="absolute right-2 top-1/2 transform bg-[#174568] -translate-y-1/2 text-white px-2 py-1 rounded-full">
                  Go
                </button>
              </div>

          <div className="overflow-x-auto -mx-4 px-8 mb-4">
            <CategoryTabs />
          </div>

<div className="w-full bg-white px-4 py-5">
  
   <Actions/>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-xl font-semibold text-[#666464]">{categoryTitle}</h1>
            <div className="flex items-center">
              <p className="text-[#174568] mr-4">{filteredGigs.length} services</p>
            
            </div>
          </div>

          {filteredGigs.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-md font-medium text-gray-700 mb-2">No services found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {paginatedGigs.map(gig => (
                  <ServiceCard key={gig.id} gig={gig} />
                ))}
              </div>
              
              {renderPagination()}
            </>
          )}
        </div>
        </div>
      </div>
      <Footer/>
      </>
    );
  };

  return isMobile ? <MobileView /> : <DesktopView />;
};

export default TodayGigs;